import React, { useEffect, useState, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  EditButton,
  StyledBookingConfirmation,
  StyledUnitImage,
  StyledCoupon,
} from "./styles";
import { Button, Divider, FlexDiv, LoaderComponent } from "../../components";
import assets from "../../assets";
import { formatDateWithOrdinal } from "../../utils/formatDateWithOrdinals";
import GuestCounter from "../GuestCounterContainer";
import useQuote from "../../hooks/useQuote";
import { BookingQuoteContext } from "../../context/BookingContextProvider";
import { InputKeys } from "../../context/types";
import { useBookingEngine } from "../../context/BookingEngineContext";
import { useTheme } from "styled-components";

const BookingConfirmationContainer: React.FC<{
  fromDate: Date | null;
  endDate: Date | null;
  unitData: any;
  switchOffFunction: () => void;
}> = ({ fromDate, endDate, unitData, switchOffFunction }) => {
  const { id } = useParams<{ id: string }>();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const bookingQuoteContext = useContext(BookingQuoteContext);
  const { bookingEngineData } = useBookingEngine();

  if (!bookingQuoteContext) {
    throw new Error("BookingQuoteContext must be used within a Provider");
  }

  const handleEditButtonClick = () => {
    switchOffFunction();
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  };

  const { setQuote } = bookingQuoteContext;
  const [guests, setGuests] = useState({ adults: 1, children: 0 });
  const [coupon, setCoupon] = useState("");
  const [withPets, setWithPets] = useState(false);

  const {
    data: quoteData,
    error: quoteError,
    couponError,
    applyCoupon,
    loading,
  } = useQuote(id!, {
    adults: guests.adults,
    children: guests.children,
    withPets,
    fromDate: fromDate?.toISOString().split("T")[0] || "",
    toDate: endDate?.toISOString().split("T")[0] || "",
    coupon: coupon || "",
  });

  useEffect(() => {
    if (fromDate && endDate) {
      // Extract UTC date components explicitly
      const formattedFromDate = new Date(
        Date.UTC(
          fromDate.getUTCFullYear(),
          fromDate.getUTCMonth(),
          fromDate.getUTCDate()
        )
      )
        .toISOString()
        .split("T")[0];

      const formattedToDate = new Date(
        Date.UTC(
          endDate.getUTCFullYear(),
          endDate.getUTCMonth(),
          endDate.getUTCDate()
        )
      )
        .toISOString()
        .split("T")[0];

      const quoteRequest = {
        adults: guests.adults,
        children: guests.children,
        withPets,
        fromDate: formattedFromDate,
        toDate: formattedToDate,
        coupon: coupon || "",
      };

      setQuote({
        [InputKeys.CHECK_IN]: quoteRequest.fromDate,
        [InputKeys.CHECK_OUT]: quoteRequest.toDate,
        [InputKeys.ADULTS]: quoteRequest.adults,
        [InputKeys.CHILDREN]: quoteRequest.children,
        [InputKeys.PETS]: quoteRequest.withPets,
      });

      console.log("🚀 Sending Quote Request:", quoteRequest);
    }
  }, [fromDate, endDate, guests, withPets, coupon, setQuote]);

  const handleApplyCoupon = async () => {
    await applyCoupon();
  };

  console.log("quote error", quoteError);
  const handleContinueToPayment = () => {
    if (!quoteData) return;

    const queryParams = new URLSearchParams({
      adults: guests.adults.toString(),
      children: guests.children.toString(),
      coupon: coupon || "",
    }).toString();

    navigate(`/payment/${id}?${queryParams}`, {
      state: {
        quoteData,
        unitData,
        fromDate: fromDate ? fromDate.toISOString().split("T")[0] : "",
        toDate: endDate ? endDate.toISOString().split("T")[0] : "",
        rentalGuardianEnabled: bookingEngineData?.rentalGuardianEnabled,
      },
    });
  };

  const placeholderImage =
    theme === "dark"
      ? assets.imagePlaceholderDark
      : assets.imagePlaceholderWhite;

  return (
    <StyledBookingConfirmation>
      <FlexDiv alignitems="center" gap="8px">
        <img
          style={{ cursor: "pointer" }}
          onClick={switchOffFunction}
          height={20}
          src={assets.GoBack}
          alt="go back icon"
          className="go-back"
        />
        <h1>Confirm Your Booking</h1>
      </FlexDiv>
      <FlexDiv gap="10px" style={{ margin: "20px 0" }} alignitems="center">
        <StyledUnitImage
          src={
            unitData?.mainImage?.image?.urls?.big
              ? unitData.mainImage.image.urls.big
              : placeholderImage
          }
          onError={(e) => {
            (e.target as HTMLImageElement).src = placeholderImage;
          }}
        />

        <FlexDiv direction="column">
          <h2>{unitData.inUseListingTitle}</h2>
          <p>
            {unitData.inUseBuildingCategory?.name}, {unitData.inUseCity},{" "}
            {unitData.inUseRegion}
          </p>
        </FlexDiv>
      </FlexDiv>
      <Divider content="Your Trip" />
      <div style={{ margin: "20px 0" }}>
        <h2>Selected Dates</h2>
        <FlexDiv justifycontent="space-between" style={{ marginTop: 10 }}>
          <p className="text-sm text-gray-600">
            {fromDate && formatDateWithOrdinal(fromDate)} -{" "}
            {endDate && formatDateWithOrdinal(endDate)}
          </p>
          <EditButton onClick={handleEditButtonClick}>Edit</EditButton>
        </FlexDiv>
      </div>
      <GuestCounter
        adults={guests.adults}
        children={guests.children}
        onGuestChange={setGuests}
        maxGuests={unitData?.inUseFeatures?.accommodates}
      />
      {unitData?.inUseFeatures?.suitablePets && (
        <FlexDiv justifycontent="space-between">
          <label
            style={{ display: "flex", alignItems: "center", width: "100%" }}
          >
            <strong>Bringing pets?</strong>
            <input
              type="checkbox"
              checked={withPets}
              onChange={(e) => setWithPets(e.target.checked)}
              disabled={!unitData.inUseFeatures.suitableForPets}
            />
          </label>
        </FlexDiv>
      )}
      <p>
        {unitData.inUseFeatures.suitableForPets
          ? "Pets are allowed in this property."
          : "Pets are not allowed in this property."}
      </p>
      <Divider />
      {quoteData && (
        <div style={{ margin: "20px 0 " }}>
          <h2>Price Breakdown</h2>
          <FlexDiv
            justifycontent="space-between"
            style={{ margin: "20px 0  0 0 " }}
          >
            <p>Total Rent</p>
            <p>
              {quoteData.currency.symbol}
              {quoteData.rentAmountWithoutDiscount}
            </p>
          </FlexDiv>
          {coupon && (
            <>
              <FlexDiv justifycontent="space-between">
                <p>Discount Amount</p>
                <p>
                  -{quoteData.currency.symbol}
                  {quoteData.discountAmount}
                </p>
              </FlexDiv>
              <Divider />
              <FlexDiv justifycontent="space-between">
                <p>Discounted Rent</p>
                <p>
                  {quoteData.currency.symbol}
                  {quoteData.rentAmount}
                </p>
              </FlexDiv>
            </>
          )}
          {quoteData.fees.map((fee: any) => (
            <FlexDiv justifycontent="space-between" key={fee.name}>
              <p>{fee.name}</p>
              <p>
                {quoteData.currency.symbol}
                {fee.amount}
              </p>
            </FlexDiv>
          ))}
        </div>
      )}
      <label style={{ fontSize: 14 }}>
        <strong>Do you have a coupon code?</strong>
      </label>
      <FlexDiv style={{ margin: "10px 0" }}>
        <StyledCoupon
          type="text"
          value={coupon}
          onChange={(e) => setCoupon(e.target.value)}
          placeholder="Enter coupon code"
        />
        <button onClick={handleApplyCoupon} className="coupon-btn">
          Apply Coupon
        </button>
      </FlexDiv>
      {couponError && <p className="text-red-500">{couponError}</p>}
      {loading ? (
        <>
          {" "}
          <div style={{ height: 30 }}></div>
          <LoaderComponent isRelative content="Calculating quote ..." />
          <div style={{ height: 30 }}></div>
        </>
      ) : (
        <Button
          onClick={handleContinueToPayment}
          disabled={loading || !quoteData}
          style={{
            height: 50,
            marginTop: 20,
            opacity: loading || !quoteData ? 0.5 : 1,
          }}
        >
          {"Continue to Payment"}
        </Button>
      )}
      {quoteError && (
        <p style={{ textAlign: "center", marginTop: 10, color: "#FF5722" }}>
          {quoteError === "The booked unit is unavailable"
            ? quoteError
            : "❗ An error happened. Please try again later."}
        </p>
      )}{" "}
      <div style={{ height: 30 }}></div>
    </StyledBookingConfirmation>
  );
};

export default BookingConfirmationContainer;
